import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  TableSortLabel,
  Menu,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import TokenIcon from './TokenIcon';
import { usePrices } from '../hooks/usePrices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { formatBalance, formatPrice } from '../utils/format';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ApexChart from 'react-apexcharts';
import { useCoinSparklines } from '../hooks/useCoinSparklines';
import { checkSuspiciousBalance, getSafePortfolioValue } from '../utils/balanceValidation';
import { isRTL } from '../i18n';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { isVirtualPortfolioEnabled } from '../constants/featureFlags';
import MarketOverviewTable from './MarketOverviewTable';

const headCells = [
  { id: 'coin', numeric: false, disablePadding: false, labelKey: 'tables.wallets.coin' },
  { id: 'balance', numeric: true, disablePadding: false, labelKey: 'tables.wallets.amount' },
  { id: 'buyPrice', numeric: true, disablePadding: false, labelKey: 'portfolio.buyPrice' },
  { id: 'price', numeric: true, disablePadding: false, labelKey: 'tables.wallets.price' },
  { id: 'marketValue', numeric: true, disablePadding: false, labelKey: 'tables.wallets.marketValue' },
  { id: 'gain24h', numeric: true, disablePadding: false, labelKey: 'tables.wallets.gain24h' },
  { id: 'gain24hPercent', numeric: true, disablePadding: false, labelKey: 'tables.wallets.gain24hPercent' },
  { id: 'gain1m', numeric: true, disablePadding: false, labelKey: 'tables.wallets.gain1m' },
  { id: 'gain1mPercent', numeric: true, disablePadding: false, labelKey: 'tables.wallets.gain1mPercent' },
  { id: 'gainAllTime', numeric: true, disablePadding: false, labelKey: 'tables.wallets.gainAllTime' },
  { id: 'gainAllTimePercent', numeric: true, disablePadding: false, labelKey: 'tables.wallets.gainAllTimePercent' },
  { id: 'percentage', numeric: true, disablePadding: false, labelKey: 'tables.portfolio.portfolioPercentage' },
  { id: 'wallets', numeric: true, disablePadding: false, labelKey: 'navigation.wallets' },
];

function descendingComparator(a, b, orderBy) {
  if (orderBy === 'gain24h' || orderBy === 'gain24hPercent' || 
      orderBy === 'gain1m' || orderBy === 'gain1mPercent' || 
      orderBy === 'gainAllTime' || orderBy === 'gainAllTimePercent') {
    // For gain columns, we need to handle undefined values
    const aValue = a[orderBy] || 0;
    const bValue = b[orderBy] || 0;
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const GroupedBalances = ({ 
  balances, 
  loading, 
  error, 
  onRefresh,
  currentPrices = {},
  formatCurrency,
  portfolioGains = {},
  onAddHoldings,
  showNumbers // <-- add this prop
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isHebrew = i18n.language === 'he';
  const { navigateWithLang } = useLanguageNavigation();
  const location = useLocation();
  // console.log(`[GroupedBalances] Component rendered with ${balances?.length || 0} balances and ${Object.keys(currentPrices).length} prices`);
  // console.log(`[GroupedBalances] Available prices:`, Object.keys(currentPrices));
  // console.log(`[GroupedBalances] Balance coins:`, balances?.map(b => b.coin) || []);
  // console.log(`[GroupedBalances] Full currentPrices object:`, currentPrices);
  
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('marketValue');
  const [viewMode, setViewMode] = React.useState(() => {
    const saved = localStorage.getItem('groupedBalancesViewMode');
    return saved || 'compact';
  }); // 'compact' or 'detailed'
  const [columnsDialogOpen, setColumnsDialogOpen] = React.useState(false);
  const [visibleColumns, setVisibleColumns] = React.useState(() => {
    const saved = localStorage.getItem('groupedBalancesVisibleColumns');
    return saved ? JSON.parse(saved) : headCells.map(cell => cell.id);
  });
  const [tempVisibleColumns, setTempVisibleColumns] = React.useState(visibleColumns);
  const [tempHiddenColumns, setTempHiddenColumns] = React.useState([]);
  
  React.useEffect(() => {
    localStorage.setItem('groupedBalancesVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  
  React.useEffect(() => {
    localStorage.setItem('groupedBalancesViewMode', viewMode);
  }, [viewMode]);



  // Calculate hidden columns
  const tempHiddenColumnsComputed = React.useMemo(() => {
    return headCells.filter(col => !tempVisibleColumns.includes(col.id)).map(col => col.id);
  }, [tempVisibleColumns]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRowClick = (coinSymbol) => {
    navigateWithLang(`coin/${coinSymbol}`);
  };

  const handleOpenColumnsDialog = () => {
    setTempVisibleColumns([...visibleColumns]);
    setTempHiddenColumns(headCells.filter(col => !visibleColumns.includes(col.id)).map(col => col.id));
    setColumnsDialogOpen(true);
  };

  const handleCloseColumnsDialog = () => {
    setColumnsDialogOpen(false);
  };

  const handleSaveColumnsDialog = () => {
    setVisibleColumns([...tempVisibleColumns]);
    setColumnsDialogOpen(false);
  };

  const handleMoveColumnUp = (index) => {
    if (index === 0) return;
    setTempVisibleColumns((prev) => {
      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      return newOrder;
    });
  };

  const handleMoveColumnDown = (index) => {
    if (index === tempVisibleColumns.length - 1) return;
    setTempVisibleColumns((prev) => {
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  };

  const handleHideColumn = (colId) => {
    if (tempVisibleColumns.length === 1) return;
    setTempVisibleColumns(tempVisibleColumns.filter(id => id !== colId));
    setTempHiddenColumns([...tempHiddenColumns, colId]);
  };

  const handleShowColumn = (colId) => {
    setTempHiddenColumns(tempHiddenColumns.filter(id => id !== colId));
    setTempVisibleColumns([...tempVisibleColumns, colId]);
  };
  
  const calculateTotalValue = (balance, coinSymbol) => {
    // Make price lookup case-insensitive
    const upperCoinSymbol = coinSymbol?.toUpperCase();
    const priceData = currentPrices[upperCoinSymbol] || currentPrices[coinSymbol];
    

    
    if (priceData && typeof priceData === 'object' && priceData.usd) {
      const result = balance * priceData.usd;

      return result;
    } else if (priceData && typeof priceData === 'number') {
      const result = balance * priceData;

      return result;
    }

    return 0;
  };

  const totalPortfolioValue = React.useMemo(() => {
    if (!balances) return 0;
    
    // Calculate total value excluding suspicious balances
    const processedBalances = balances.map(balance => {
      const marketValue = calculateTotalValue(balance.total_balance, balance.coin);
      return { ...balance, marketValue };
    });
    
    return getSafePortfolioValue(processedBalances);
  }, [balances, currentPrices]);


  const processedBalances = React.useMemo(() => {
    if (!balances) return [];
    
    return balances.map(balance => {
      const marketValue = calculateTotalValue(balance.total_balance, balance.coin);
      // Make price lookup case-insensitive
      const upperCoinSymbol = balance.coin?.toUpperCase();
      const priceData = (currentPrices[upperCoinSymbol] || currentPrices[balance.coin]) || 0;
      const price = typeof priceData === 'object' && priceData.usd ? priceData.usd : (typeof priceData === 'number' ? priceData : 0);
      
      // Check for suspicious balance
      const suspiciousCheck = checkSuspiciousBalance({
        coin: balance.coin,
        total_balance: balance.total_balance,
        marketValue: marketValue
      });
      
      // Calculate percentage only for non-suspicious balances
      const safeTotalValue = getSafePortfolioValue(balances.map(b => ({
        ...b,
        marketValue: calculateTotalValue(b.total_balance, b.coin)
      })));
      const percentage = safeTotalValue > 0 ? (marketValue / safeTotalValue) * 100 : 0;
      
      // Use average buy price from the balances response (calculated from transaction history)
      const buyPrice = balance.avg_buy_price || 0;
      
      const coinGains = portfolioGains[balance.coin];
      
      return {
        ...balance,
        balance: balance.total_balance,
        buyPrice,
        price,
        marketValue,
        percentage,
        wallets: balance.wallet_count,
        gain24h: coinGains?.gain_24h || 0,
        gain24hPercent: coinGains?.gain_24h_percent || 0,
        gain1m: coinGains?.gain_1m || 0,
        gain1mPercent: coinGains?.gain_1m_percent || 0,
        gainAllTime: coinGains?.gain_all_time || 0,
        gainAllTimePercent: coinGains?.gain_all_time_percent || 0,
        // Suspicious balance information
        suspicious: suspiciousCheck.isSuspicious,
        warningReason: suspiciousCheck.reason,
        warningSeverity: suspiciousCheck.severity,
        warningDetails: suspiciousCheck.details
      };
    });
  }, [balances, currentPrices, totalPortfolioValue, portfolioGains]);

  // Prepare coins and percent changes for sparklines
  const coins = React.useMemo(() => balances.map(b => b.coin), [balances]);
  const percentChanges = React.useMemo(() => {
    const map = {};
    for (const b of balances) {
      map[b.coin] = b.gain24hPercent || 0;
    }
    return map;
  }, [balances]);

  // Get coins in visual order (sorted by market value)
  const sortedCoins = React.useMemo(() => {
    const sortedBalances = stableSort(processedBalances, getComparator(order, orderBy));
    return sortedBalances.map(b => b.coin);
  }, [processedBalances, order, orderBy]);

  const { charts: sparklineCharts, getColor: getSparklineColor, loading: sparklinesLoading, progress: sparklinesProgress } = useCoinSparklines(sortedCoins, percentChanges, { points: 24, refreshMinutes: 10, concurrency: 5, priorityCount: 10 });

  // Update processedBalances with sparkline-based 24h gains
  const processedBalancesWithSparklineGains = React.useMemo(() => {
    return processedBalances.map(balance => {
      // Calculate 24h gain from sparkline data
      let gain24hPercent = balance.gain24hPercent;
      const chartData = sparklineCharts[balance.coin];
      
      
      
      if (chartData && Array.isArray(chartData.data) && chartData.data.length > 0) {
        const firstPrice = chartData.data[0].close;
        if (firstPrice > 0 && balance.price > 0) {
          gain24hPercent = ((balance.price - firstPrice) / firstPrice) * 100;
          
          
        }
      }
      
      return {
        ...balance,
        gain24h: (gain24hPercent / 100) * balance.marketValue,
        gain24hPercent: gain24hPercent,
      };
    });
  }, [processedBalances, sparklineCharts]);

  // Calculate global gains and percentages
  const globalGains = React.useMemo(() => {
    if (!processedBalancesWithSparklineGains.length) return { gain24h: 0, gain24hPercent: 0, gain1m: 0, gain1mPercent: 0, gainAllTime: 0, gainAllTimePercent: 0 };
    const totalMarketValue = processedBalancesWithSparklineGains.reduce((sum, b) => sum + (b.marketValue || 0), 0);
    const gain24h = processedBalancesWithSparklineGains.reduce((sum, b) => sum + (b.gain24h || 0), 0);
    const gain1m = processedBalancesWithSparklineGains.reduce((sum, b) => sum + (b.gain1m || 0), 0);
    const gainAllTime = processedBalancesWithSparklineGains.reduce((sum, b) => sum + (b.gainAllTime || 0), 0);
    // Weighted percent: sum(marketValue * percent) / sum(marketValue)
    const gain24hPercent = totalMarketValue ? processedBalancesWithSparklineGains.reduce((sum, b) => sum + ((b.marketValue || 0) * (b.gain24hPercent || 0)), 0) / totalMarketValue : 0;
    const gain1mPercent = totalMarketValue ? processedBalancesWithSparklineGains.reduce((sum, b) => sum + ((b.marketValue || 0) * (b.gain1mPercent || 0)), 0) / totalMarketValue : 0;
    const gainAllTimePercent = totalMarketValue ? processedBalancesWithSparklineGains.reduce((sum, b) => sum + ((b.marketValue || 0) * (b.gainAllTimePercent || 0)), 0) / totalMarketValue : 0;
    return {
      gain24h,
      gain24hPercent,
      gain1m,
      gain1mPercent,
      gainAllTime,
      gainAllTimePercent
    };
  }, [processedBalancesWithSparklineGains]);

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!balances || balances.length === 0) {
    return (
      <Box>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
              {t('portfolio.portfolioEmpty')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {t('portfolio.connectWalletAndAddHoldings')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigateWithLang('add-holdings')}
              size="large"
            >
              {t('walletTransactions.addHoldings')}
            </Button>
          </CardContent>
        </Card>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 2, px: 2 }}>
          {t('portfolio.securityDisclaimer')}
        </Typography>
        
        {/* Virtual Trading Card */}
        {isVirtualPortfolioEnabled() && (
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ textAlign: 'center', py:3 }}>
              <Typography variant="h6" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('portfolio.virtualTradingTitle')}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {t('portfolio.virtualTradingDescription')}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigateWithLang('v-portfolio')}
                size="medium"
              >
                {t('portfolio.startVirtualTrading')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Market Overview Table */}
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <MarketOverviewTable />
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Render based on view mode
  const isDetailedView = viewMode === 'detailed';
  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 1 } }}>
      <Box display="flex" alignItems="center" mb={2} width="100%" minWidth={0} justifyContent="space-between">
          <Box>
            <Chip
              label={t('walletTransactions.addHoldings')}
              color="primary"
              size="small"
              variant="outlined"
              onClick={() => navigateWithLang('add-holdings')}
              sx={{ fontWeight: 500, fontSize: '0.9rem' }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={t('walletTransactions.detailed')}
              color="primary"
              size="small"
              variant={isDetailedView ? 'filled' : 'outlined'}
              onClick={() => setViewMode(isDetailedView ? 'compact' : 'detailed')}
              sx={{ 
                fontWeight: 500, 
                fontSize: '0.9rem',
                px: 2, // Add horizontal padding
                py: 0.5 // Add vertical padding
              }}
            />
            <Tooltip title="Column settings">
              <IconButton onClick={handleOpenColumnsDialog} size="small">
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
      </Box>

      {!isDetailedView ? (
        // Compact view (original design)
        <Box display="flex" flexDirection="column" gap={0} sx={{ bgcolor: '#fff', borderRadius: 1 }}>
          {stableSort(processedBalancesWithSparklineGains, getComparator(order, orderBy)).map((balance, index) => (
            <Box 
              key={balance.coin + '-' + index} 
              onClick={() => handleRowClick(balance.coin)}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                py: { xs: 0.5, sm: 1.5 }, 
                px: { xs: 0.5, sm: 2 }, 
                borderBottom: index < processedBalances.length - 1 ? '1px solid #f0f0f0' : 'none',
                width: '100%', 
                cursor: 'pointer', 
                '&:hover': { background: '#fafbfc' },
                '&:last-child': { borderBottom: 'none' }
              }}
            >
              {/* Coin icon and info */}
              <Box display="flex" alignItems="center" minWidth={60} flex={2}>
                <TokenIcon coinSymbol={balance.coin} size={22} sx={{ mx: 0.75 }} />
                <Box sx={{ position: 'relative', mr: isRTL() ? 2 : 0, ml: isRTL() ? 0 : 2 }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.1rem' }, lineHeight: 1.2 }}>{balance.coin}</Typography>
                    {balance.suspicious && (
                      <Tooltip title={balance.warningReason || 'Suspicious balance detected'}>
                        <WarningAmberIcon 
                          sx={{ 
                            fontSize: { xs: '0.9rem', sm: '1rem' }, 
                            color: balance.warningSeverity === 'error' ? 'error.main' : 'warning.main',
                            cursor: 'help'
                          }} 
                        />
                      </Tooltip>
                    )}
                  </Box>
                  {balance.coin_name && (
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.2, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>{balance.coin_name}</Typography>
                  )}
                  <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.2, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                    {showNumbers ? formatBalance(balance.total_balance) : '•••••••'}
                  </Typography>
                  {/* Show account type if available */}
                  {balance.account_type && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip
                        label={balance.account_type === 'trade' ? 'Trading' : balance.account_type === 'main' ? 'Wallet' : balance.account_type}
                        size="small"
                        variant="outlined"
                                                  sx={{
                            height: 16,
                            fontSize: '0.6rem',
                            '& .MuiChip-label': { px: 0.5 },
                            bgcolor: balance.account_type === 'trade' ? 'success.50' : balance.account_type === 'main' ? 'primary.50' : 'default.50',
                            borderColor: balance.account_type === 'trade' ? 'success.main' : balance.account_type === 'main' ? 'primary.main' : 'default.main',
                            color: balance.account_type === 'trade' ? 'success.main' : balance.account_type === 'main' ? 'primary.main' : 'default.main'
                          }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              {/* Sparkline */}
              <Box flex={2} minWidth={40} maxWidth={70} sx={{ mx: { xs: 0.5, sm: 1 } }}>
                {(() => {
                  try {
                    // Extract and validate data
                    const chartData = sparklineCharts[balance.coin];
                    if (!chartData || !Array.isArray(chartData.data)) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    // Extract close values and validate them
                    const closes = chartData.data
                      .map((d, index) => {
                        if (!d || typeof d !== 'object') {
                          return null;
                        }
                        if (typeof d.close !== 'number' || !isFinite(d.close)) {
                          return null;
                        }
                        return d.close;
                      })
                      .filter(v => v !== null && typeof v === 'number' && isFinite(v));



                    // Need at least 2 valid data points for a meaningful sparkline
                    if (closes.length < 2) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    const minValue = Math.min(...closes);
                    const maxValue = Math.max(...closes);
                    if (minValue <= 0 || maxValue <= 0 || !isFinite(minValue) || !isFinite(maxValue)) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    const finalData = closes.map(val => {
                      if (typeof val !== 'number' || !isFinite(val) || val <= 0) return null;
                      return val;
                    }).filter(v => v !== null);

                    if (finalData.length < 2) {
                      return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                    }

                    const sparklineData = [...finalData];
                    
                    if (typeof balance.price === 'number' && isFinite(balance.price) && balance.price > 0) {
                      const lastChartPrice = finalData[finalData.length - 1];
                      const priceDiff = Math.abs(balance.price - lastChartPrice) / lastChartPrice;
                      if (priceDiff > 0.001) {
                        sparklineData.push(balance.price);
                      }
                    }

                    const firstValue = sparklineData[0];
                    const lastValue = sparklineData[sparklineData.length - 1];
                    const trendColor = lastValue >= firstValue ? '#21b26f' : '#d32f2f';
                    const annotations = firstValue ? {
                      yaxis: [
                        {
                          y: firstValue,
                          borderColor: '#888',
                          strokeDashArray: 2,
                          label: { show: false }
                        }
                      ]
                    } : {};

                    return (
                      <ApexChart
                        type="area"
                        height={35}
                        width={60}
                        series={[
                          {
                            data: sparklineData,
                            color: trendColor,
                          }
                        ]}
                        options={{
                          chart: { 
                            sparkline: { enabled: true },
                            animations: { enabled: false } // Disable animations to prevent issues
                          },
                          stroke: { 
                            width: 2, 
                            curve: 'straight', 
                            colors: [trendColor] 
                          },
                          fill: { 
                            type: 'gradient', 
                            gradient: { 
                              shadeIntensity: 1, 
                              opacityFrom: 0.3, 
                              opacityTo: 0, 
                              stops: [0, 100], 
                              colorStops: [] 
                            }, 
                            colors: [trendColor] 
                          },
                          colors: [trendColor],
                          markers: { size: 0 },
                          tooltip: { enabled: false },
                          grid: { show: false },
                          xaxis: { 
                            labels: { show: false }, 
                            axisBorder: { show: false }, 
                            axisTicks: { show: false } 
                          },
                          yaxis: { 
                            labels: { show: false },
                            min: Math.min(...sparklineData),
                            max: Math.max(...sparklineData)
                          },
                          annotations,
                        }}
                      />
                    );
                  } catch (error) {
                    console.error(`Error rendering sparkline for ${balance.coin}:`, error, {
                      chartData: sparklineCharts[balance.coin],
                      errorStack: error.stack
                    });
                    return <Box sx={{ width: 60, height: 35, bgcolor: '#f5f5f5', borderRadius: 1 }} />;
                  }
                })()}
              </Box>
              {/* Current price and % change */}
              <Box flex={2} minWidth={60} textAlign="center" sx={{ pr: { xs: 0.5, sm: 1 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.92rem', sm: '1rem' }, lineHeight: 1.2 }}>
                  {balance.price >= 1
                    ? ('$' + (Number.isInteger(balance.price)
                        ? balance.price.toLocaleString()
                        : balance.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      ))
                    : ('$' + balance.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }).replace(/0+$/, '').replace(/\.$/, ''))
                  }
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.2, fontSize: { xs: '0.7rem', sm: '0.85rem' } }}>
                  {showNumbers ? ('$' + Math.round(balance.marketValue).toLocaleString()) : '•••••••'}
                </Typography>
              </Box>
              {/* Percentage change chip */}
              <Box flex={1} minWidth={40} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Chip
                  label={
                    typeof balance.gain24hPercent === 'number'
                      ? `${balance.gain24hPercent >= 0 ? '+' : ''}${balance.gain24hPercent.toFixed(1)}%`
                      : '+0.0%'
                  }
                  color={(balance.gain24hPercent || 0) >= 0 ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: { xs: '0.72rem', sm: '0.85rem' }, minWidth: 60, height: { xs: 22, sm: 28 }, px: { xs: 0.5, sm: 1 }, borderRadius: 0 }}
                  style={{ minWidth: 60, overflow: 'visible', textOverflow: 'unset' }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        // Detailed view (table format)
        <TableContainer component={Paper} sx={{ 
          bgcolor: '#fff', 
          borderRadius: 1, 
          maxHeight: '60vh', // Set max height for scrolling
          overflow: 'auto', 
          direction: isRTL() ? 'rtl' : 'ltr',
          // iOS WebKit sticky fix - smooth scrolling
          WebkitOverflowScrolling: 'touch',
          // Ensure proper sticky header behavior
          '& .MuiTableHead-root': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#e9ecef'
          },
          // Ensure first column is sticky for horizontal scrolling
          '& .MuiTableCell-root:first-of-type': {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: '#fff'
          },
          '& .MuiTableHead-root .MuiTableCell-root:first-of-type': {
            backgroundColor: '#e9ecef',
            zIndex: 3
          }
        }}>
          <Table stickyHeader sx={{ direction: isRTL() ? 'rtl' : 'ltr' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e9ecef' }}>
                {headCells
                  .filter(headCell => visibleColumns.includes(headCell.id))
                  .map((headCell, index) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? 'right' : (isRTL() ? 'right' : 'left')}
                      padding={headCell.disablePadding ? 'none' : 'normal'}
                      sortDirection={orderBy === headCell.id ? order : false}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.85rem', // smaller font
                        backgroundColor: headCell.id === 'coin' ? '#e9ecef' : '#e9ecef',
                        borderRight: headCell.id === 'coin' && !isRTL() ? '1px solid #e0e0e0' : 'none',
                        borderLeft: headCell.id === 'coin' && isRTL() ? '1px solid #e0e0e0' : 'none',
                        whiteSpace: 'nowrap', // prevent wrapping
                        padding: '12px 10px', // increased padding for taller header
                        lineHeight: 1.2, // tighter line height
                        height: '56px' // explicit height for taller header
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={createSortHandler(headCell.id)}
                      >
                        {t(headCell.labelKey)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(processedBalancesWithSparklineGains, getComparator(order, orderBy)).map((balance, index) => (
                <TableRow
                  key={balance.coin + '-' + index}
                  onClick={() => handleRowClick(balance.coin)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&:last-child td, &:last-child th': { border: 0 },
                    // Ensure first row has proper spacing from sticky header
                    '&:first-of-type': {
                      '& td': {
                        borderTop: 'none'
                      }
                    }
                  }}
                >
                  {visibleColumns.includes('coin') && (
                    <TableCell
                      sx={{
                        backgroundColor: '#fff',
                        borderRight: !isRTL() ? '1px solid #e0e0e0' : 'none',
                        borderLeft: isRTL() ? '1px solid #e0e0e0' : 'none'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TokenIcon coinSymbol={balance.coin} size={24} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{balance.coin}</Typography>
                          {balance.coin_name && (
                            <Typography variant="caption" color="textSecondary">{balance.coin_name}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.includes('balance') && (
                    <TableCell align="right">
                      {showNumbers ? formatBalance(balance.total_balance) : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('buyPrice') && (
                    <TableCell align="right">
                      {showNumbers ? formatCurrency(balance.buyPrice) : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('price') && (
                    <TableCell align="right">
                      {showNumbers ? formatCurrency(balance.price) : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('marketValue') && (
                    <TableCell align="right">
                      {showNumbers ? formatCurrency(balance.marketValue) : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('gain24h') && (
                    <TableCell align="right">
                      {showNumbers ? formatCurrency(balance.gain24h) : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('gain24hPercent') && (
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={(balance.gain24hPercent || 0) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {showNumbers ? `${(balance.gain24hPercent || 0) >= 0 ? '+' : ''}${(balance.gain24hPercent || 0).toFixed(2)}%` : '•••••••'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('gain1m') && (
                    <TableCell align="right">
                      {showNumbers ? formatCurrency(balance.gain1m) : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('gain1mPercent') && (
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={(balance.gain1mPercent || 0) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {showNumbers ? `${(balance.gain1mPercent || 0) >= 0 ? '+' : ''}${(balance.gain1mPercent || 0).toFixed(2)}%` : '•••••••'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('gainAllTime') && (
                    <TableCell align="right">
                      {showNumbers ? formatCurrency(balance.gainAllTime) : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('gainAllTimePercent') && (
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={(balance.gainAllTimePercent || 0) >= 0 ? 'success.main' : 'error.main'}
                      >
                        {showNumbers ? `${(balance.gainAllTimePercent || 0) >= 0 ? '+' : ''}${(balance.gainAllTimePercent || 0).toFixed(2)}%` : '•••••••'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('percentage') && (
                    <TableCell align="right">
                      {showNumbers ? `${(balance.percentage || 0).toFixed(2)}%` : '•••••••'}
                    </TableCell>
                  )}
                  {visibleColumns.includes('wallets') && (
                    <TableCell align="right">
                      {balance.wallets || 0}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Column Chooser Dialog */}
      <Dialog open={columnsDialogOpen} onClose={handleCloseColumnsDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common.columns')}</DialogTitle>
        <DialogContent>
          <List>
            {(tempVisibleColumns || []).map((colId, index) => {
              const col = headCells.find(c => c.id === colId);
              return (
                <ListItem
                  key={colId}
                  style={{ display: 'flex', alignItems: 'center' }}
                  secondaryAction={
                    <>
                      <IconButton edge="end" onClick={() => handleMoveColumnUp(index)} disabled={index === 0}>
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleMoveColumnDown(index)} disabled={index === (tempVisibleColumns || []).length - 1}>
                        <ArrowDownwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleHideColumn(colId)} disabled={(tempVisibleColumns || []).length === 1}>
                        <VisibilityOffIcon />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText primary={col?.labelKey ? t(col.labelKey) : colId} />
                </ListItem>
              );
            })}
          </List>
          <Divider sx={{ my: 1 }} />
          <List>
            {(tempHiddenColumnsComputed || []).map((colId) => {
              const col = headCells.find(c => c.id === colId);
              return (
                <ListItem key={colId} secondaryAction={
                  <IconButton edge="end" onClick={() => handleShowColumn(colId)}>
                    <VisibilityIcon />
                  </IconButton>
                }>
                  <ListItemText primary={col?.labelKey ? t(col.labelKey) : colId} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseColumnsDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveColumnsDialog} variant="contained" autoFocus>{t('common.saveChanges')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupedBalances; 